import { registerErrorDataGatherer } from "#skyward_error";
import { ensureLiveAreaAnnounced } from "./skyward.base.extensions";

interface ActivityIndicatorSettings
{
    silent?: boolean;
    hideGlass?: boolean;
}

interface JQueryAjaxSettingsWithIndicator extends JQuery.AjaxSettings
{
    activityIndicator?: ActivityIndicatorSettings;
}

const classNames = 
{
    initialLoad: "initialLoad",
    loading: "loading",
    longLoad: "longLoad"
} as const;

const accessibilityAnnouncer = (function ()
{
    const LOADING_MESSAGE = "Loading...";
    const announcementThresholdMs = 1000;
    const periodicAnnouncementIntervalMs = 5000;
    const liveRegionElement: JQuery<HTMLElement> = $('<div>',
    {
        'aria-live': 'polite',
        'class': 'sr-only',
        'id': 'loadingAnnouncer'
    });
    liveRegionElement.appendTo(document.body);

    let announcementTimer: number | undefined;

    return {
        start()
        {
            if (announcementTimer === undefined)
            {
                announceLoading();
            }
        },

        complete()
        {
            clearAnnouncements();

            // Only announce completion if we actually started announcing loading
            if (liveRegionElement?.text().startsWith(LOADING_MESSAGE))
            {
                announceComplete();
            }
        }
    };

    function clearAnnouncements(): void
    {
        window.clearTimeout(announcementTimer);
        announcementTimer = undefined;
    };

    function announceLoading(): void
    {
        announcementTimer = window.setTimeout(function announce ()
        {
            ensureLiveAreaAnnounced(liveRegionElement, LOADING_MESSAGE);
            announcementTimer = window.setTimeout(announce, periodicAnnouncementIntervalMs);
        }, announcementThresholdMs);
    };

    function announceComplete(): void
    {
        liveRegionElement.text('Loading complete.');
    };
})();

const stateManager = (function ()
{
    const noGlassClassname = "noGlass";
    const activityWaitDelay = 100;
    let initialLoadComplete = false;
    let waitingForActivity = false;
    let isManualOverride = false;
    let isActivity = false;
    let activityWaitTimer: number | undefined;

    let lastActivityFinishedTime = 0;
    const activityFinishedPromises: Array<() => void> = [];

    return { //bracket must be on same line as return statement 
        startTime: new Date().getTime(),
        domReady: false,

        startActivity(): void
        {
            window.clearTimeout(activityWaitTimer);

            if (initialLoadComplete) 
            {
                isActivity = true;
                ajaxActivity.start();
            }
        },

        /* debounce activity complete */
        waitForActivity(): void
        {
            window.clearTimeout(activityWaitTimer);

            waitingForActivity = true;

            activityWaitTimer = window.setTimeout(function ()
            {
                waitingForActivity = false;
                completeActivity();
            }, activityWaitDelay);
        },

        toggleGlass(showGlass?: boolean): void
        {
            const localShowGlass = showGlass ?? element.hasClass(noGlassClassname);
            element.toggleClass(noGlassClassname, !localShowGlass);
        },

        startManualOverride(): void
        {
            isManualOverride = true;
            manualOverride.start();
        },

        completeManualOverride(): void
        {
            isManualOverride = false;
            if (isReady())
            {
                manualOverride.complete();
                notifyListenersOfCompletion();
            }
        },

        forceComplete(): void
        {
            initialLoadComplete = true;
            isManualOverride = false;
            isActivity = false;
            waitingForActivity = false;
            if (activityWaitTimer !== undefined)
            {
                window.clearTimeout(activityWaitTimer);
                activityWaitTimer = undefined;
            }
            initialLoad.complete();
            ajaxActivity.complete();
            manualOverride.complete();
            notifyListenersOfCompletion();
        },

        completeInitialLoad(): void
        {
            initialLoadComplete = true;
            notifyListenersOfCompletion();
        },

        async waitForAllActivitiesToComplete(extraTime = 0): Promise<void>
        {
            let delay = extraTime;
            while (true) 
            {
                const timeoutPromise = new Promise<void>(res => setTimeout(res, delay));
                const finishedPromise = isReady() ? Promise.resolve() : new Promise<void>(res => { activityFinishedPromises.push(res); });

                await Promise.all([timeoutPromise, finishedPromise]);
                const elapsed = performance.now() - lastActivityFinishedTime;

                if (elapsed >= extraTime) 
                {
                    return;
                }
                delay = extraTime - elapsed;
            }
        },
    };

    function isReady()
    {
        return stateManager.domReady && !waitingForActivity && !isManualOverride && initialLoadComplete && !isActivity;
    }

    function completeActivity()
    {
        isActivity = false;
        if (!isReady())
        {
            return;
        }

        ajaxActivity.complete();
        notifyListenersOfCompletion();
    };

    function notifyListenersOfCompletion()
    {
        if (!isReady()) 
        {
            return;
        }
        lastActivityFinishedTime = performance.now();
        for (const promiseResolution of activityFinishedPromises) 
        {
            promiseResolution();
        }
        activityFinishedPromises.length = 0;
    };
})();

const element = (function ()
{
    const indicatorId = "LoadingIndicator";
    let indicatorElement: JQuery<HTMLElement> | undefined;

    return { //bracket must be on same line as return statement 
        addClass(className: string): void
        {
            getElement().addClass(className);
        },

        removeClass(className: string): void
        {
            getElement().removeClass(className);
        },

        toggleClass(className: string, toggle?: boolean): void
        {
            getElement().toggleClass(className, toggle);
        },

        hasClass(className: string): boolean
        {
            return getElement().hasClass(className);
        },

        setAttribute(attr: string, val: string): void
        {
            getElement().attr(attr, val);
        }
    };

    function getElement(): JQuery<HTMLElement>
    {
        if (indicatorElement === undefined) 
        {
            indicatorElement = $('#' + indicatorId);
        }
        return indicatorElement;
    };
})();

const ajaxProcessor = (function ()
{
    const defaultActivityIndicatorSettings: ActivityIndicatorSettings =
    {
        silent: false,
        hideGlass: false
    } as const;

    let openRequests = 0;
    let numActivitiesWithGlass = 0;

    return {  //bracket must be on same line as return statement 
        requestSent(_event: JQuery.TriggeredEvent, _xhr: JQuery.jqXHR, settings: JQueryAjaxSettingsWithIndicator): void 
        {
            const localSettings = $.extend({}, defaultActivityIndicatorSettings, settings.activityIndicator);
            if (localSettings.silent) 
            {
                return;
            }
            if (!localSettings.hideGlass) 
            {
                numActivitiesWithGlass++;
            }

            openRequests++;

            const showGlass = numActivitiesWithGlass !== 0;
            stateManager.toggleGlass(showGlass);

            stateManager.startActivity();
        },

        requestComplete(_event: JQuery.TriggeredEvent, _xhr: JQuery.jqXHR, settings: JQueryAjaxSettingsWithIndicator): void 
        {
            const localSettings = $.extend({}, defaultActivityIndicatorSettings, settings.activityIndicator);
            if (localSettings.silent) 
            {
                return;
            }

            openRequests = Math.max(openRequests - 1, 0); // requestComplete may have been called without requestSent

            if (!localSettings.hideGlass) 
            {
                numActivitiesWithGlass = Math.min(Math.max(numActivitiesWithGlass - 1, 0), openRequests);
            }

            const hideGlass = numActivitiesWithGlass === 0;
            stateManager.toggleGlass(!hideGlass);

            if (!ajaxProcessor.hasOpenRequest()) 
            {
                stateManager.waitForActivity();
            }
        },

        hasOpenRequest(): boolean 
        {
            return openRequests > 0;
        },

        get openRequests(): number 
        {
            return openRequests;
        },

        get numActivitiesWithGlass(): number 
        {
            return numActivitiesWithGlass;
        },
    };
})();

const initialLoad = (function ()
{
    const longActivityThreshold = 1600;

    return {  //bracket must be on same line as return statement 
        start(): void 
        {
            $(document.body).on('keydown', preventTabWhileLoading);
            longLoad.start();

            $(function () 
            {
                stateManager.domReady = true;
                window.performance?.mark?.("initialLoadComplete");
                initialLoad.complete();
                stateManager.completeInitialLoad();
                if (ajaxProcessor.hasOpenRequest()) 
                {
                    ajaxActivity.start();
                }
            });
        },

        complete(): void 
        {
            const body = $(document.body);
            body.off('keydown', preventTabWhileLoading);

            updateElementLoad(classNames.initialLoad, false);
            doneLoading(checkActivityThreshold());
            accessibilityAnnouncer.complete();
        }
    };

    function checkActivityThreshold(): boolean
    {
        return new Date().getTime() - stateManager.startTime > longActivityThreshold;
    };

    function preventTabWhileLoading(e: JQuery.KeyDownEvent): void 
    {
        if (e.key === "Tab")
        {
            e.stopPropagation();
            e.preventDefault();
        }
    }
})();

const createTimer = function (longActivityThreshold: number = 600)
{
    let overrideStartTime: number;

    return {  //bracket must be on same line as return statement 
        start(): void 
        {
            overrideStartTime = new Date().getTime();
            updateElementLoad(classNames.loading, true);
            longLoad.start();
            accessibilityAnnouncer.start();
        },

        complete(): void 
        {
            updateElementLoad(classNames.loading, false);
            doneLoading(checkActivityThreshold());
            accessibilityAnnouncer.complete();
        }
    };

    function checkActivityThreshold(): boolean
    {
        return new Date().getTime() - overrideStartTime > longActivityThreshold;
    };
}
const manualOverride = createTimer();
const ajaxActivity = createTimer();

const longLoad = (function ()
{
    let longLoadTimer: number | undefined;
    const longLoadDelay = 25000;

    return {  //bracket must be on same line as return statement                   
        start(): void
        {
            window.clearTimeout(longLoadTimer);
            longLoadTimer = window.setTimeout(() => 
            {
                updateElementLoad(classNames.longLoad, true);
            }, longLoadDelay);
        },

        complete(): void
        {
            window.clearTimeout(longLoadTimer);
            longLoadTimer = undefined;
            updateElementLoad(classNames.longLoad, false);
        }
    };
})();

const doneLoading = (function ()
{
    let doneLoadingTimer: number | undefined;
    const animationDuration = 300;
    const className = "done";

    return function (shouldAnimate: boolean): void 
    {
        window.clearTimeout(doneLoadingTimer);

        longLoad.complete();

        if (!shouldAnimate) 
        {
            element.removeClass(className);
        }
        else 
        {
            element.addClass(className);

            doneLoadingTimer = window.setTimeout(() => 
            {
                element.removeClass(className);
            }, animationDuration);
        }
    };
})();

// Initialize module
registerErrorDataGatherer("activityindicator", gatherDataForError);
init();

// Export the wait function
export const waitForAllActivitiesToComplete = (extraTime?: number): Promise<void> =>
    stateManager.waitForAllActivitiesToComplete(extraTime);

function gatherDataForError()
{
    return {
        openRequests: ajaxProcessor.openRequests, 
        numActivitiesWithGlass: ajaxProcessor.numActivitiesWithGlass
    };
};

// This is needed for browsers that support bfcache (back/forward cache).
window.onpageshow = function (event: PageTransitionEvent): void
{
    /* hide the indicator if the page loaded from cache */
    if (event.persisted) 
    {
        stateManager.forceComplete();
        accessibilityAnnouncer.complete();
    }
};

function updateElementLoad(className: string, isStarting: boolean): void 
{
    element.toggleClass(className, isStarting);
}

// Initialization function
function init()
{
    setEvents();
    initialLoad.start();
};

function setEvents(): void
{
    $(document)
        .ajaxSend(ajaxProcessor.requestSent)
        .ajaxComplete(ajaxProcessor.requestComplete)
        .on('showLoadingIndicator', stateManager.startManualOverride)
        .on('hideLoadingIndicator', stateManager.completeManualOverride);
};
